import { Camera, ChevronDown, Copy, Download, FileUp, History, LogIn, LogOut, Redo2, Save, Search, Send, TriangleAlert, Undo2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { businessOptionsForCompany, companyOptionsForState, employeeBelongsToCompany, unitBelongsToCompany } from "../lib/company";
import { useOrgStore } from "../state/orgStore";
import type { AppMode, CompanyFilter, OrgState } from "../types/org";
import { Modal } from "./Modal";

interface ToolbarProps {
  mode: AppMode;
  onImport: () => void;
  onIssues: () => void;
  onVersions: () => void;
  onSnapshots: () => void;
  onExport: () => void;
}

const ALL_MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);
type ConfirmAction = "saveDraft" | "publish";

export function Toolbar({ mode, onImport, onIssues, onVersions, onSnapshots, onExport }: ToolbarProps) {
  const [openMenu, setOpenMenu] = useState<"company" | "business" | undefined>();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>();
  const [actionBusy, setActionBusy] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  const [feedback, setFeedback] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const state = useOrgStore((store) => store.state);
  const latestDraft = useOrgStore((store) => store.latestDraft);
  const previousDraft = useOrgStore((store) => store.previousDraft);
  const latestPublished = useOrgStore((store) => store.latestPublished ?? store.published);
  const previousPublished = useOrgStore((store) => store.previousPublished);
  const saveDraft = useOrgStore((store) => store.saveDraft);
  const publish = useOrgStore((store) => store.publish);
  const undo = useOrgStore((store) => store.undo);
  const redo = useOrgStore((store) => store.redo);
  const loadPeriod = useOrgStore((store) => store.loadPeriod);
  const copyPreviousPeriodToCurrent = useOrgStore((store) => store.copyPreviousPeriodToCurrent);
  const setCompanyFilter = useOrgStore((store) => store.setCompanyFilter);
  const setBusinessUnitFilter = useOrgStore((store) => store.setBusinessUnitFilter);
  const updateSearchQuery = useOrgStore((store) => store.setSearchQuery);
  const applySearchMatch = useOrgStore((store) => store.applySearchMatch);

  const company = state.companyFilter ?? "Qisda";
  const pendingCount = state.issues.filter((issue) => issue.status === "open").length;
  const optionContext = useMemo(
    () => [state, latestDraft, previousDraft, latestPublished, previousPublished].filter(Boolean) as OrgState[],
    [latestDraft, latestPublished, previousDraft, previousPublished, state],
  );
  const availableYears = useMemo(() => uniqueSorted(optionContext.map((item) => item.year)), [optionContext]);
  const availableMonths = useMemo(() => {
    const months = uniqueSorted(optionContext.filter((item) => item.year === state.year).map((item) => item.month));
    return uniqueSorted([...ALL_MONTHS, ...months]);
  }, [optionContext, state.year]);
  const availableCompanies = useMemo(() => companyOptionsForState(state), [state]);
  const businessOptions = useMemo(() => businessOptionsForCompany(state, company), [company, state]);
  const isEmptyPeriod = Object.keys(state.units).length === 0;

  useEffect(() => {
    if (!feedback) return undefined;
    const timeout = window.setTimeout(() => setFeedback(""), 3200);
    return () => window.clearTimeout(timeout);
  }, [feedback]);

  function changeYear(value: string) {
    void loadPeriod(Number(value), state.month, mode);
  }

  function changeMonth(value: string) {
    void loadPeriod(state.year, Number(value), mode);
  }

  function runSearch(query = searchInputRef.current?.value ?? state.searchQuery ?? "") {
    const match = findSearchMatch(state, query);
    updateSearchQuery(query);
    if (match) applySearchMatch(match.unitId, match.employeeId);
  }

  async function confirmToolbarAction() {
    if (!confirmAction) return;
    setActionBusy(true);
    try {
      if (confirmAction === "saveDraft") {
        await saveDraft();
        setFeedback(`${state.year}/${state.month} 草稿已儲存`);
      } else {
        await publish();
        setFeedback(`${state.year}/${state.month} 組織圖已發布`);
      }
      setConfirmAction(undefined);
    } catch (error) {
      console.error(error);
      setFeedback(confirmAction === "saveDraft" ? "儲存草稿失敗，請稍後再試" : "發布失敗，請稍後再試");
    } finally {
      setActionBusy(false);
    }
  }

  async function copyPreviousPeriod() {
    setCopyBusy(true);
    try {
      const copied = await copyPreviousPeriodToCurrent();
      setFeedback(copied ? `${state.year}/${state.month} 已由前一月份複製建立` : "找不到前一月份可複製的組織圖資料");
    } catch (error) {
      console.error(error);
      setFeedback("複製前一月份失敗，請稍後再試");
    } finally {
      setCopyBusy(false);
    }
  }

  return (
    <>
      <header className="toolbar">
        <div className="brand">
          <div>
            <strong>{mode === "draft" ? "組織圖管理後台" : "組織圖正式區"}</strong>
            <span>{state.year}/{state.month} {mode === "draft" ? "草稿" : "正式版"}</span>
          </div>
        </div>

        <section className="toolbar-filters" aria-label="組織圖篩選">
          <label className="toolbar-field year-field">
            <span>年份</span>
            <select value={state.year} onChange={(event) => changeYear(event.currentTarget.value)}>
              {availableYears.map((year) => <option key={year} value={year}>{year}</option>)}
            </select>
          </label>

          <label className="toolbar-field month-field">
            <span>月份</span>
            <select value={state.month} onChange={(event) => changeMonth(event.currentTarget.value)}>
              {availableMonths.map((month) => <option key={month} value={month}>{month}</option>)}
            </select>
          </label>

          <DropdownField
            className="company-field"
            label="公司"
            open={openMenu === "company"}
            value={company}
            options={availableCompanies.map((item) => ({ label: item, value: item }))}
            onOpenChange={(open) => setOpenMenu(open ? "company" : undefined)}
            onSelect={(value) => {
              setCompanyFilter(value as CompanyFilter);
              setOpenMenu(undefined);
            }}
          />

          <DropdownField
            className="business-field"
            label="事業群/單位"
            open={openMenu === "business"}
            value={state.businessUnitFilter ?? ""}
            options={[{ label: "請選擇", value: "" }, ...businessOptions.map((item) => ({ label: item, value: item }))]}
            onOpenChange={(open) => setOpenMenu(open ? "business" : undefined)}
            onSelect={(value) => {
              setBusinessUnitFilter(value);
              setOpenMenu(undefined);
            }}
          />

          <form
            className="search-box"
            onSubmit={(event) => {
              event.preventDefault();
              runSearch();
            }}
          >
            <button
              type="submit"
              aria-label="搜尋"
              onClick={(event) => {
                event.preventDefault();
                runSearch();
              }}
            >
              <Search size={16} />
            </button>
            <input
              ref={searchInputRef}
              name="search"
              value={state.searchQuery ?? ""}
              onChange={(event) => updateSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                runSearch(event.currentTarget.value);
              }}
              placeholder="搜尋單位、工號或姓名"
            />
          </form>
        </section>

        <section className="toolbar-actions" aria-label="功能操作">
          {mode === "draft" ? (
            <>
              {isEmptyPeriod ? (
                <button className="toolbar-button secondary-action-button" onClick={copyPreviousPeriod} disabled={copyBusy}>
                  <Copy size={16} /> {copyBusy ? "複製中..." : "由前月複製建立"}
                </button>
              ) : null}
              <button className="toolbar-button action-import" onClick={onImport}><FileUp size={16} /> 匯入資料</button>
              <button className="toolbar-button badge-button action-issues" onClick={onIssues}><TriangleAlert size={16} /> 待處理<b>{pendingCount}</b></button>
              <button className="toolbar-button action-version secondary-action-button" onClick={onVersions}><History size={16} /> 版本</button>
              <button className="toolbar-button action-snapshot secondary-action-button" onClick={onSnapshots}><Camera size={16} /> 快照</button>
              <button className="icon-button" onClick={undo} title="復原" aria-label="復原"><Undo2 size={16} /></button>
              <button className="icon-button" onClick={redo} title="重做" aria-label="重做"><Redo2 size={16} /></button>
              <button className="toolbar-button outline action-save" onClick={() => setConfirmAction("saveDraft")}><Save size={16} /> 儲存草稿</button>
              <button className="toolbar-button primary action-publish" onClick={() => setConfirmAction("publish")}><Send size={16} /> 發布</button>
              <button className="toolbar-button secondary-action-button" onClick={() => { window.location.href = "/"; }}><LogOut size={16} /> 回正式區</button>
            </>
          ) : (
            <>
              <button className="toolbar-button action-version secondary-action-button" onClick={onVersions}><History size={16} /> 版本</button>
              <button className="toolbar-button action-export secondary-action-button" onClick={onExport}><Download size={16} /> 匯出</button>
              <button className="toolbar-button primary" onClick={() => { window.location.href = "/admin"; }}><LogIn size={16} /> 前往管理後台</button>
            </>
          )}
        </section>
      </header>
      {feedback ? <div className="toolbar-feedback" role="status">{feedback}</div> : null}
      {confirmAction ? (
        <ConfirmToolbarActionModal
          action={confirmAction}
          year={state.year}
          month={state.month}
          busy={actionBusy}
          onCancel={() => !actionBusy && setConfirmAction(undefined)}
          onConfirm={confirmToolbarAction}
        />
      ) : null}
    </>
  );
}

function ConfirmToolbarActionModal({
  action,
  year,
  month,
  busy,
  onCancel,
  onConfirm,
}: {
  action: ConfirmAction;
  year: number;
  month: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isSaveDraft = action === "saveDraft";
  return (
    <Modal title={isSaveDraft ? "確認儲存草稿" : "確認發布"} onClose={onCancel}>
      <div className="action-confirm-body">
        <p>{isSaveDraft ? `儲存 ${year}/${month} 草稿？` : `發布 ${year}/${month} 組織圖？`}</p>
        <small>{isSaveDraft ? "草稿會成為草稿區最新儲存版本。" : "發布後會成為正式區最新瀏覽版本。"}</small>
      </div>
      <div className="modal-actions action-confirm-actions">
        <button className="secondary-action" onClick={onCancel} disabled={busy}>取消</button>
        <button className="primary-action" onClick={onConfirm} disabled={busy}>
          {busy ? "處理中..." : isSaveDraft ? "確認儲存草稿" : "確認發布"}
        </button>
      </div>
    </Modal>
  );
}

function DropdownField({
  className,
  label,
  open,
  value,
  options,
  onOpenChange,
  onSelect,
}: {
  className: string;
  label: string;
  open: boolean;
  value: string;
  options: Array<{ label: string; value: string }>;
  onOpenChange: (open: boolean) => void;
  onSelect: (value: string) => void;
}) {
  const selected = options.find((option) => option.value === value) ?? options[0];
  return (
    <div className={`toolbar-field custom-field ${className}`}>
      <span>{label}</span>
      <div className="custom-select">
        <button type="button" className="custom-select-trigger" onClick={() => onOpenChange(!open)} aria-expanded={open}>
          <b>{selected?.label ?? value}</b>
          <ChevronDown size={15} />
        </button>
        {open ? (
          <div className="custom-select-menu">
            {options.map((option) => (
              <button
                key={`${label}-${option.value || "all"}`}
                type="button"
                className={option.value === value ? "is-selected" : ""}
                onClick={() => onSelect(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function findSearchMatch(state: OrgState, query: string) {
  const needle = query.trim().toLowerCase();
  if (!needle) return undefined;
  const company = state.companyFilter ?? "Qisda";
  const selectedBusiness = state.businessUnitFilter ?? "";
  const units = Object.values(state.units).filter((unit) => !unit.isUnlinked && unitBelongsToCompany(state, unit, company) && (!selectedBusiness || matchesToken(unit.buBg, selectedBusiness)));

  const unit = units.find((item) => [item.code, item.nameZh, item.nameEn, item.buBg, item.managerNameZh, item.managerNameEn].some((value) => includesText(value, needle)));
  if (unit) return { unitId: unit.id };

  const employee = Object.values(state.employees).find((item) => {
    const unit = item.unitId ? state.units[item.unitId] : undefined;
    return employeeBelongsToCompany(state, item, company) &&
      (!selectedBusiness || matchesToken(unit?.buBg, selectedBusiness) || matchesToken(item.buBg, selectedBusiness)) &&
      [item.employeeNo, item.chineseName, item.englishName, item.jobTitle, item.internalTitle, item.externalTitle, item.departmentCode, item.departmentNameZh, item.departmentNameEn].some((field) => includesText(field, needle));
  });
  return employee?.unitId ? { unitId: employee.unitId, employeeId: employee.id } : undefined;
}

function includesText(value: unknown, needle: string) {
  return String(value ?? "").toLowerCase().includes(needle);
}

function matchesToken(value: unknown, token: string) {
  return String(value ?? "").trim().toLowerCase() === token.trim().toLowerCase();
}

function uniqueSorted(values: number[]) {
  return [...new Set(values)].sort((a, b) => a - b);
}
